import type { ReactNode } from "react";
import Icon from "../Icon/Icon";
import "./Stat.css";

export interface StatProps {
  /** small grey text on top, e.g. "Guarantors" */
  label: ReactNode;
  /** the figure itself: text, a number or a <Badge /> */
  value?: ReactNode;
  /** small icon before the label (an <Icon /> or <img />) */
  icon?: ReactNode;
  /** makes the whole stat a button and shows an arrow after the value */
  onClick?: () => void;
  /** anything after the value, e.g. a small Edit button */
  action?: ReactNode;
  className?: string;
}

/**
 * One figure with its label, for header strips and summaries.
 * Example: <Stat icon={<Icon name="users" />} label="Guarantors" value={2} onClick={open} />
 * Put several inside <div className="ds-stat-row"> so they line up.
 */
const Stat = ({
  label,
  value,
  icon,
  onClick,
  action,
  className = "",
}: StatProps) => {
  const body = (
    <>
      <span className="ds-stat__label">
        {icon}
        <span className="ds-stat__label-text">{label}</span>
      </span>
      <span className="ds-stat__value">
        {value}
        {onClick && (
          <Icon name="chevronRight" size={16} className="ds-stat__chevron" />
        )}
        {!onClick && action}
      </span>
    </>
  );

  if (!onClick) {
    return <div className={`ds-stat ${className}`.trim()}>{body}</div>;
  }

  // clickable: the label and value are one button; the action (a button of
  // its own) sits next to it, never inside it
  const button = (
    <button
      type="button"
      className={`ds-stat ${action ? "" : className}`.trim()}
      onClick={onClick}
    >
      {body}
    </button>
  );

  return action ? (
    <div className={`ds-stat-group ${className}`.trim()}>
      {button}
      <span className="ds-stat__action">{action}</span>
    </div>
  ) : (
    button
  );
};

export default Stat;
